import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { getConsultorioBranding } from '../api/consultorioBranding';

export const CONSULTORIO_BRANDING_CHANGED = 'consultorio-branding-changed';

const ConsultorioBrandingContext = createContext(null);

export function useConsultorioBranding() {
  const ctx = useContext(ConsultorioBrandingContext);
  if (!ctx) {
    throw new Error('useConsultorioBranding debe usarse dentro de ConsultorioBrandingProvider');
  }
  return ctx;
}

export function ConsultorioBrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadBranding = useCallback(async () => {
    if (!user?.tenant_id) {
      setBranding(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await getConsultorioBranding();
      setBranding(data);
    } catch {
      setBranding({
        tenant_id: user.tenant_id,
        name: user.tenant_name || 'Consultorio',
        slug: user.tenant_slug || '',
        logo_url: user.tenant_logo_url || null,
        can_edit_logo: user.consultorio_role === 'administrador',
      });
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id, user?.tenant_name, user?.tenant_slug, user?.tenant_logo_url, user?.consultorio_role]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  useEffect(() => {
    const onChanged = () => loadBranding();
    window.addEventListener(CONSULTORIO_BRANDING_CHANGED, onChanged);
    return () => window.removeEventListener(CONSULTORIO_BRANDING_CHANGED, onChanged);
  }, [loadBranding]);

  const refreshBranding = useCallback(async () => {
    await loadBranding();
    window.dispatchEvent(new CustomEvent(CONSULTORIO_BRANDING_CHANGED));
  }, [loadBranding]);

  const value = {
    branding,
    loading,
    refreshBranding,
    displayName: branding?.name || user?.tenant_name || 'Consultorio',
    logoUrl: branding?.logo_url || user?.tenant_logo_url || null,
    canEditLogo: branding?.can_edit_logo ?? user?.consultorio_role === 'administrador',
  };

  return (
    <ConsultorioBrandingContext.Provider value={value}>
      {children}
    </ConsultorioBrandingContext.Provider>
  );
}
